/* istanbul ignore file */
import numeral from 'numeral';
import Vue from '../vue';

export const isFunction = value => typeof value === 'function';
export const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);
export const isString = value => typeof value === 'string' || value instanceof String;

export const propToBool = prop => prop === '' || prop;
export const propToFnc = prop => {
  if (isFunction(prop)) return prop;

  if (prop) {
    try {
      return new Function('value', `return value.${prop};`);
    } catch (e) {
      return f => f[prop];
    }
  }

  return f => f;
}
export const valueToFnc = value => isFunction(value) ? value : (() => value);

export const isBetween = (val, from, to) => val >= from && val <= to;

export const errorDebug = (error) => {
  if (error !== false && error.toString() !== 'Cancel') {
    if (process.env.NODE_ENV === 'development') console.error(error);
    var errorData = error.response?.data || error.message;
    Vue.notify({ type: 'error', title: 'Error', text: errorData || error || 'Error' });
  }
}

export const Debouncer = function (milliseconds) {
  var finishFnc = null;
  var fncs = [];
  let date = null;
  const startCountdown = function () {
    date = new Date(Date.now() + milliseconds);
    var rec = function () {
      if (date != null) { // eslint-disable-line
        if (date < new Date()) {
          date = null;
          var localFinishFnc = finishFnc;
          Promise
            .all(fncs.map(f => f()))
            .then(() => {
              if (localFinishFnc) {
                localFinishFnc();
              }
            })
            .catch(err => console.error(err));
          fncs = [];
          finishFnc = null;
        } else {
          setTimeout(rec, 0);
        }
      }
    };
    rec();
  };
  this.clear = () => {
    date = null;
    fncs = [];
    return this;
  };
  this.push = (fnc) => {
    fncs.push(fnc);
    startCountdown();
    return this;
  };
  this.onFinishFnc = (fnc) => {
    finishFnc = fnc;
    return this;
  };
  this.hasOnFinishFnc = () => finishFnc != null; // eslint-disable-line
  this.isProcessing = () => fncs.length > 0;
  this.pause = () => date = new Date(Date.now() + 100000000000);
  this.setTimer = newMiliseconds => milliseconds = newMiliseconds;

  this.resetTimer = startCountdown;
};

export const Accelerator = function (callbackFnc, acceleration) {
  var isBreaking = false;
  var previousTimeStamp;
  var previousSpeed = 0;
  var to;
  var position = 0;
  this.isRunning = false;
  var onAnimation = timestamp => {
    var multiplier = position > to ? -1 : 1;
    this.isRunning = true;
    if (!previousTimeStamp) previousTimeStamp = timestamp;
    var elapsed = timestamp - previousTimeStamp;

    if (!isBreaking) {
      var breakDist = Math.pow(previousSpeed, 2) / (2 * acceleration);
      var ds = multiplier * (to - position) - breakDist;
      var tAcc = (-2 * previousSpeed + Math.sqrt(Math.pow(2 * previousSpeed, 2) + 4 * acceleration * ds)) / (2 * acceleration);
      if (tAcc < elapsed) {
        isBreaking = true;
        let speed = previousSpeed + acceleration * tAcc;
        position += multiplier * (previousSpeed + speed) * tAcc / 2;
        previousSpeed = speed;
        previousTimeStamp += tAcc;
        elapsed -= tAcc;
      }
    }

    var currentAcceleration = isBreaking ? -acceleration : acceleration;
    let speed = previousSpeed + currentAcceleration * elapsed;

    if (isBreaking && speed <= 0.01) {
      this.isRunning = false;
      previousSpeed = 0;
      previousTimeStamp = 0;
      return;
    }

    position += multiplier * (previousSpeed + speed) * elapsed / 2;

    callbackFnc(position);

    previousSpeed = speed;
    previousTimeStamp = timestamp;

    requestAnimationFrame(onAnimation);
  }

  this.setFrom = newFrom => {
    position = newFrom;
    return this;
  };
  this.setTo = newTo => {
    isBreaking = false;
    to = newTo;
    if (!this.isRunning) {
      requestAnimationFrame(onAnimation);
    }
    return this;
  };
}

export const createFormData = (obj) => {
  const fd = new FormData();
  Object.keys(obj)
    .forEach(key => fd.append(key, obj[key]));
  return fd;
};

export const hashCode = (obj) => {
  const str = JSON.stringify(obj);
  let hash = 0;
  if (!str.length) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash &= hash; // Convert to 32bit integer
  }
  return hash;
};

export const toKeyValue = obj => Object.keys(obj)
  .reduce((acc, key) => {
    acc.push({ key, value: obj[key] });
    return acc;
  }, []);

export const tryParse = obj => {
  try {
    return JSON.parse(obj);
  } catch (e) {
    return null;
  }
}

export const getAllParentNodes = el => el.parentNode ? [el.parentNode, ...getAllParentNodes(el.parentNode)].filter(f => f.tagName) : [];
export const getAllChildrens = function getAllChildrens(element) {
  var arr = Array.from(element.children);
  return arr.mapMany(f => [f].pushMany(getAllChildrens(f)));
};
export const getAllChildNodes = function getAllChildNodes(element) {
  var arr = Array.from(element.childNodes);
  return arr.mapMany(f => [f].pushMany(getAllChildNodes(f)));
};

export const getObjectFromProps = (obj, properties) => properties.reduce((acc, key) => { acc[key] = obj[key]; return acc; }, {});

export const randomString = format => format.replace(/[Xx]/g, c => {
  var num = Math.floor(Math.random() * 26 + 65);
  var char = String.fromCharCode(num);
  return c === 'X' ? char : char.toLowerCase();
});

export const formatDate = date => {
  return new Date(date).toLocaleDateString();
}

export const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const isNullOrEmpty = function (str) {
  return (str == null || !str.toString().trim())
}

export const generateUUID = () => {
  const iterate = (length, charset, result = '') => {
    const string = `${result}${charset.charAt(Math.floor(Math.random() * charset.length))}`;
    return string.length === length ? string : iterate(length, charset, string);
  };
  const randomX = (length) => iterate(length, '0123456789ABCDEF');
  const randomM = () => iterate(1, '12345');
  const randomN = () => iterate(1, '89AB');
  return `${randomX(8)}-${randomX(4)}-${randomM()}${randomX(3)}-${randomN()}${randomX(3)}-${randomX(12)}`;
};

export const customNumberFormat = value => numeral(value).format('0,0' + ((value * 10) % 10 > 0 ? '.0' : ''));

export const searchWordRegex = searchWord => {
  var replacers = [
    { from: '\\', to: '\\\\' },
    { from: '(', to: '\\(' },
    { from: ')', to: '\\)' },
    { from: '^', to: '\\^' },
  ];
  return RegExp(replacers.reduce(function (acc, f) { return acc.replaceAll(f.from, f.to); }, searchWord || ''), 'gi');
}

export const findSearchWordSplitted = (sentance, searchWordRegex) => {
  if (!sentance) return [];
  var spls = [0, ...Array.from(sentance.matchAll(searchWordRegex)).flatMap(f => [f.index, f.index + f[0].length]), sentance.length];
  return spls.map((f, ind) => ({
    word: sentance.substring(f, spls[ind + 1]),
    isMatch: ind % 2 === 1,
  })).filter(f => f.word)
};

export const interpolateFnc = (counts, values) => {
  var min = counts.min();
  var max = counts.max();
  var delta = max - min;
  return count => {
    var percent = Math.max(0, (count - min) / delta) * 100;
    var to = (values.find(f => f.percent >= percent) || values.last());
    var from = values[Math.max(values.indexOf(to) - 1, 0)];
    if (to === from) return from.value;
    percent = (percent - from.percent) / (to.percent - from.percent);

    return Object.keys(from.value)
      .reduce((acc, key) => {
        acc[key] = Math.round(from.value[key] + (to.value[key] - from.value[key]) * percent);
        return acc;
      }, Array.isArray(from.value) ? [] : {});
  }
};

export const sanitize = (input, replacement) => {
  replacement = replacement || '';

  function isHighSurrogate(codePoint) {
    return codePoint >= 0xd800 && codePoint <= 0xdbff;
  }

  function isLowSurrogate(codePoint) {
    return codePoint >= 0xdc00 && codePoint <= 0xdfff;
  }

  // Truncate string by size
  function truncate(string, length) {
    if (typeof string !== "string") {
      throw new Error("Input must be string");
    }

    var charLength = string.length;
    var codePoint;
    var segment; // eslint-disable-line

    for (var i = 0; i < charLength; i += 1) {
      codePoint = string.charCodeAt(i);
      segment = string[i];

      if (isHighSurrogate(codePoint) && isLowSurrogate(string.charCodeAt(i + 1))) {
        i += 1;
        segment += string[i];
      }
    }

    return string.substring(0, length);
  }

  var illegalRe = /[/?<>\\:*|":]/g;
  var controlRe = /[\x00-\x1f\x80-\x9f]/g; // eslint-disable-line 
  var reservedRe = /^\.+$/;
  var windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
  var windowsTrailingRe = /[. ]+$/;

  var sanitized = input
    .replace(illegalRe, replacement)
    .replace(controlRe, replacement)
    .replace(reservedRe, replacement)
    .replace(windowsReservedRe, replacement)
    .replace(windowsTrailingRe, replacement);
  return truncate(sanitized, 255);
};

export const valueDisplayFormat = {
  currency: value => numeral(value).format('$0,0'),
  number: value => customNumberFormat(value),
  percentages: value => numeral(value).format('0.0%'),
  text: value => value,
  default: value => value,
}

export const toFormData = (obj, formData, parentKey) => {
  formData = formData || new FormData();

  Object.keys(obj).forEach(key => {
    const value = obj[key];

    const formKey = parentKey ? `${parentKey}[${key}]` : key;

    if (value instanceof File) {
      formData.append(formKey, value);
    } else if (Array.isArray(value)) {
      value.forEach((element, index) => {
        toFormData(element, formData, `${formKey}[${index}]`);
      });
    } else if (typeof value === 'object' && value !== null) {
      toFormData(value, formData, formKey);
    } else {
      if (value) formData.append(formKey, value);
    }
  });

  return formData;
}

export const objectsAreEqual = (first, second) => {
  if (Object.keys(first).length !== Object.keys(second).length) {
    return false;
  }

  for (var prop in first) {
    if (first[prop] !== second[prop]) {
      // Values may be different due to different object references. Do deep check.
      if (!(first[prop] instanceof Object && second[prop] instanceof Object)
        || !objectsAreEqual(first[prop], second[prop])) {
        return false;
      }
    }
  }

  return true;
};

export const getTextWidth = (font) => (text) => {
  // re-use canvas object for better performance
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

const btoa = str => new Buffer.from(str, 'binary').toString('base64');
export const objectToHash = obj => `#${btoa(JSON.stringify(obj))}`;
const atob = str => Buffer.from(str, 'base64').toString('binary');
export const hashToObject = hash => tryParse(atob(hash.substring(1)));

export const trimString = (maxLength, str) => str.length < maxLength ? str : (str.substring(0, maxLength - 3).trim() + '...');

export const joinSingleStrings = splitted =>
  splitted
    .reduce((acc, f, ind) => {
      if (f.length === 1 && ((splitted[ind - 1] || '').length === 1))
        acc = [...acc.head(), `${acc.last()}${f}`];
      else acc.push(f);
      return acc;
    }, []);

export const mergeFilters = (to, from) => Object.keys(from)
  .reduce((acc, key) => ({
    ...acc,
    [key]: !isObject(from[key]) ? from[key] : mergeFilters(to[key], from[key]),
  }), to || {});


export const resolveAfterInactivity = maxInactivityTime => new Promise(resolve => {
  console.log('Running an incativity check');
  const debouncer = new Debouncer(10);

  const getLastActiveTime = () => tryParse(localStorage.getItem('lastActiveTime') || JSON.stringify(new Date()));
  const setLastActiveTime = () => debouncer.clear().push(() => localStorage.setItem('lastActiveTime', JSON.stringify(new Date())));
  const getInactivityTime = () => new Date() - new Date(getLastActiveTime());

  if (getInactivityTime() >= maxInactivityTime) {
    localStorage.removeItem('lastActiveTime');
    return resolve();
  }

  function checkInactivity() {
    window.addEventListener('mousemove', setLastActiveTime);
    window.addEventListener('scroll', setLastActiveTime);
    window.addEventListener('keydown', setLastActiveTime);

    const inactivityInterval = setInterval(() => {
      if (getInactivityTime() >= maxInactivityTime) {
        window.removeEventListener('mousemove', setLastActiveTime);
        window.removeEventListener('scroll', setLastActiveTime);
        window.removeEventListener('keydown', setLastActiveTime);
        clearInterval(inactivityInterval);
        localStorage.removeItem('lastActiveTime');
        resolve();
      }
    }, 1000); // Check every 1 second for inactivity
  }

  checkInactivity();
});